import config from '../config';
import { getRequiredCultures } from './required-cultures';
import { ApiError } from './ApiError';
import { Tenant } from './model/tenant';
import { getArea } from './get-content-area';
import { getDefaultHeaders } from './helpers/api-helpers';
import { isUrl } from '../libs/utils';

export async function getAdtifUrl(input: string, tenant: Tenant): Promise<string> {
  if (isUrl(input)) {
    const url = new URL(input);
    if (!url.searchParams.has('templateDeliveryMode')) {
      url.searchParams.append('templateDeliveryMode', 'adtif');
    }
    return url.toString();
  }

  const isTemplateToken = input.includes('..');
  let templateToken = input;
  if (!isTemplateToken) {
    templateToken = await getLatestTemplateToken(input, tenant);
  }

  return `${config.deliveryServiceUrl}/api/v1/accounts/${tenant.accountId}/contentAreas/${tenant.areaId}/assets:deliver?requestor=localization-ui&templateToken=${templateToken}&templateDeliveryMode=adtif`;
}

export async function getDesignAssets(adtifUrl: string, culture: string, tenant: Tenant): Promise<any> {
  const cultures = await getRequiredCultures(tenant);
  const escaped = escape(adtifUrl);
  const endpoint = `/api/v2/Localizations?adtifUrl=${escaped}`;
  const url = `${config.localizationUIServerUrl}${endpoint}`;

  const results = await fetchExportData(url);

  if (!results.ok) {
    //error
    throw new ApiError('get DesignAssets failed', results.status);
  }

  const isGuid = (candidate: string): boolean => {
    const r = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return r.test(candidate);
  };

  const json = await results.json();
  return json.map((x: any) => ({
    localizationId: x.localizationId,
    isNewPlatform: isGuid(x.localizationId),
    value: x.cultures[culture],
    adtif: adtifUrl,
    missingCultures: substract(
      cultures.map((c) => c.toLowerCase()),
      Object.keys(x.cultures),
    ),
  }));
}

async function getLatestTemplateToken(dtsTemplateId: string, tenant: Tenant): Promise<string> {
  const response = await getAssetsByDtsTemplateId(dtsTemplateId, tenant);
  return response?.data?.[0]?.customProperties?.legacyIdentifiers?.panels?.find(
    (panel: any) => panel.dtsTemplateId === dtsTemplateId,
  )?.latestTemplateToken;
}

export async function getAssetsByDtsTemplateId(dtsTemplateId: string, tenant: Tenant): Promise<any> {
  const area = getArea(tenant.accountId, tenant.areaId);
  const searchParams = new URLSearchParams({
    requestor: 'localization-ui',
    customProperties: JSON.stringify({
      ['legacyIdentifiers.panels.dtsTemplateId']: [`${dtsTemplateId}`],
    }),
  });
  const response = await fetch(
    `${config.camsUrl}/api/v4/${area.contentAuthoringAccountId}/${
      area.contentAuthoringAreaId
    }/assets?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: getDefaultHeaders(),
    },
  );

  return await response.json();
}

export async function getAssetsByTemplateToken(templateToken: string, tenant: Tenant): Promise<any> {
  const area = getArea(tenant.accountId, tenant.areaId);
  const searchParams = new URLSearchParams({
    requestor: 'localization-ui',
    customProperties: JSON.stringify({
      ['legacyIdentifiers.panels.templateTokens']: [`${templateToken}`],
    }),
  });
  const response = await fetch(
    `${config.camsUrl}/api/v4/${area.contentAuthoringAccountId}/${
      area.contentAuthoringAreaId
    }/assets?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: getDefaultHeaders(),
    },
  );

  return await response.json();
}

const fetchExportData = async (url: string): Promise<Response> =>
  await fetch(url, {
    method: 'GET',
    headers: getDefaultHeaders(),
  });

const substract = (arr1: string[], arr2: string[]): string[] => arr1.filter((el) => !arr2.includes(el));
